import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";

/**
 * StepIndicator component renders a series of steps with circular indicators.
 * Each step can be in one of three states: active, completed, or inactive.
 *
 * @param {Array} stepsConfig - Array of step objects, each containing a `key` and `label`.
 * @param {number} currentStep - The index (1-based) of the current active step.
 * @param {string} [containerClass=""] - Custom CSS class for the container.
 * @param {string} [labelClass=""] - Custom CSS class for the step labels.
 * @param {Function} [stepClick] - Called with the step index when a step is clicked. Omit it for a read-only rail: the steps then carry no pointer cursor and no click handler.
 * @param {boolean[]} [stepsWithErrors=[]] - Boolean array; true at index i means step i has validation errors.
 * @param {boolean[]} [stepsWithWarning=[]] - Boolean array; true at index i means step i has a non-blocking warning (e.g. resume validation flagged it) — distinct color from error, lower priority.
 *
 * @returns {JSX.Element} The rendered StepIndicator component.
 */
export const StepIndicator = ({
  stepsConfig,
  currentStep,
  containerClass = "",
  labelClass = "",
  stepClick,
  stepsWithErrors = [],
  stepsWithWarning = [], // NEW — optional, defaults to empty so old callers are unaffected
  stepsReady = null,
  stepsCompleted = [], // NEW — optional, defaults to empty so old callers are unaffected
}) => {
  const scrollRef = useRef(null);
  const [scrollState, setScrollState] = useState({ left: false, right: false });

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    setScrollState({
      left: el.scrollLeft > 0,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollState();
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    el.addEventListener("scroll", updateScrollState);

    return () => {
      observer.disconnect();
      el.removeEventListener("scroll", updateScrollState);
    };
  }, [updateScrollState, stepsConfig.length]);

  const scrollSteps = (direction) => {
    scrollRef.current?.scrollBy({ left: direction * 220, behavior: "smooth" });
  };

  // Keep the active step visible when the rail overflows. Done by hand rather
  // than with scrollIntoView, which also scrolls the page vertically.
  useEffect(() => {
    const el = scrollRef.current;
    const child = el?.children?.[currentStep - 1];
    if (!el || !child) return;

    const target = child.offsetLeft - el.clientWidth / 2 + child.clientWidth / 2;
    el.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
  }, [currentStep]);

  const hasOverflow = scrollState.left || scrollState.right;

  return (
    <div className={cn("mb-6 px-2 w-full flex items-center gap-1", containerClass)}>
      {hasOverflow && (
        <Button
          type="button"
          aria-label="Show previous steps"
          onClick={() => scrollSteps(-1)}
          disabled={!scrollState.left}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md p-0 text-text-muted hover:bg-background-tertiary disabled:cursor-not-allowed disabled:opacity-30"
        >
          <ChevronLeft size={16} />
        </Button>
      )}

      {/* `overflow-x-auto` makes overflow-y `auto` too, so this box clips top and
          bottom as well as the sides. The padding is what the active step's ring
          (2px ring + 2px offset) and the label's descenders live in — without it
          both were shaved off, on every screen that shows these steps. */}
      <div ref={scrollRef} className="no-scrollbar flex items-start w-full overflow-x-auto pt-1.5 pb-1">
        {stepsConfig.map(({ key, label }, i) => {
          const isActive = i === currentStep - 1;
          const isLast = i === stepsConfig.length - 1;
          const hasError = !!stepsWithErrors[i];
          const hasWarning = !!stepsWithWarning[i]; // NEW
          const isCompleted = !!stepsCompleted[i]; // NEW

          const isPending = stepsReady !== null && stepsReady[i] !== true;

          // Priority: error (red) > warning (orange) > completed (blue) > pending (amber) > ready (green)
          const ringColor = hasError ? "#e05252" : hasWarning ? "#f97316" : isCompleted ? "#3f9d6b" : isPending ? "#d99a3a" : "#3b82f6";

          const labelColor = hasError
            ? "text-[#e05252]"
            : hasWarning
              ? "text-[#f97316]"
              : isCompleted
                ? "text-[#3f9d6b]"
                : isPending
                  ? "text-[#d99a3a]"
                  : "text-[#3b82f6]";

          return (
            // `stepClick` is optional: a rail that only reports progress reads
            // as a plain row, rather than offering a pointer cursor and then
            // throwing "stepClick is not a function" on the click.
            <div
              key={key}
              className={cn("flex items-start", stepClick ? "cursor-pointer" : "cursor-default", !isLast ? "flex-1" : "flex-shrink-0")}
              onClick={stepClick ? () => stepClick(i) : undefined}
            >
              <div className="flex flex-col items-center flex-shrink-0 min-w-[110px]">
                <div
                  className={cn(
                    "rounded-full flex-shrink-0 w-[30px] h-[30px]",
                    isActive && "ring-2 ring-offset-2 ring-offset-background-secondary ring-white/50 rounded-full",
                  )}
                >
                  <div className={cn("w-full h-full rounded-full p-[4px]", isPending && "animate-pulse")} style={{ background: ringColor }}>
                    <div className="w-full h-full rounded-full flex items-center justify-center">
                      <span className="text-text-base text-sm font-semibold">{i + 1}</span>
                    </div>
                  </div>
                </div>

                <span
                  className={cn("mt-1.5 text-center leading-tight block text-[10px] whitespace-nowrap", isActive ? "font-bold" : "font-medium", labelColor, labelClass)}
                >
                  {label}
                </span>
              </div>

              {!isLast && <div className="flex-1 h-[2px] bg-[#CAC4D0] mt-[14px] mx-1 sm:mx-2 min-w-[8px]" />}
            </div>
          );
        })}
      </div>

      {hasOverflow && (
        <Button
          type="button"
          aria-label="Show more steps"
          onClick={() => scrollSteps(1)}
          disabled={!scrollState.right}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md p-0 text-text-muted hover:bg-background-tertiary disabled:cursor-not-allowed disabled:opacity-30"
        >
          <ChevronRight size={16} />
        </Button>
      )}
    </div>
  );
};
